import classnames from 'classnames';
import React, { Children, useState, isValidElement, useEffect, createElement as _createElement } from 'react';
import { __DEV__ } from "../../helper/dev.js";
import styles from "./Tab.module.js";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Creates an onClick function, triggering the onClick of <TabItem />, additionally passing the index.
 * Sets active tab to the selected tab.
 */
const makeHandleClick = (childOnClick, index, setActiveTab) => {
  return event => {
    if (childOnClick) {
      childOnClick(event, index);
    }
    setActiveTab(index);
  };
};
export const Tab = ({
  children,
  className,
  scrollable,
  activeIndex = 0,
  tabIndex = 0,
  renderHiddenTabContent = false
}) => {
  const [activeTab, setActiveTab] = useState(activeIndex);
  useEffect(() => {
    setActiveTab(activeIndex);
  }, [activeIndex]);

  // extract props from all valid children
  const childProps = Children.map(children, child => {
    if (/*#__PURE__*/isValidElement(child)) {
      return child.props;
    }
    return null;
  })?.filter(Boolean) ?? [];
  if (__DEV__ && activeTab > childProps.length - 1) {
    // eslint-disable-next-line no-console
    console.error(`[Illegal State]: passed (presumably) initial "activeIndex" as <Tab activeIndex={${activeIndex}} /> with less than ${activeIndex} children present.
      Did you maybe pass children that are not <TabItem />?`);
  }
  const classes = classnames(styles.tab, className);

  // currently rendered tabpanel content
  const currentChildren = childProps[activeTab]?.children || null;
  return /*#__PURE__*/_jsxs("div", {
    className: classes,
    children: [/*#__PURE__*/_jsx("div", {
      ...getTabListProps(scrollable),
      children: childProps.map(({
        title,
        className,
        onClick: childOnClick,
        tabIndex,
        disabled
      }, index) => {
        // whether this iteration is selected
        const active = index === activeTab;
        const onClick = makeHandleClick(childOnClick, index, setActiveTab);
        return /*#__PURE__*/_createElement("button", {
          type: "button",
          ...getTabProps({
            index,
            className,
            active,
            onClick,
            tabIndex
          }),
          key: index,
          disabled: disabled,
          role: "tab"
        }, title);
      })
    }), renderHiddenTabContent ? /*#__PURE__*/_jsx(_Fragment, {
      children: childProps.map(({
        children
      }, index) => /*#__PURE__*/_jsx("section", {
        ...getTabPanelProps(index, index),
        className: classnames(index !== activeTab && styles.hidden),
        children: children
      }, index))
    }) : /*#__PURE__*/_jsx("section", {
      ...getTabPanelProps(activeTab, tabIndex),
      children: currentChildren
    })]
  });
};

// aria-attributes - for indepth, see https://www.w3.org/TR/wai-aria-practices-1.2/#tabpanel
// implementation based on https://github.com/adobe/react-spectrum/blob/main/packages/%40react-aria/tabs/src/useTabs.ts

export const getTabListProps = scrollable => {
  const className = classnames(styles.tabList, {
    [styles.scrollable]: scrollable
  });
  return {
    className,
    'aria-orientation': 'horizontal',
    role: 'tablist'
  };
};
export const getTabProps = ({
  index,
  active,
  className: passedClassName,
  onClick,
  tabIndex,
  disabled
}) => {
  const tabId = generateId(index, 'tab');
  const tabPanelId = generateId(index, 'tabPanel');
  const className = classnames(styles.item, {
    [styles.active]: active
  }, passedClassName);
  return {
    className,
    role: 'tab',
    'aria-selected': active,
    'aria-disabled': false,
    'aria-controls': active ? tabPanelId : undefined,
    id: tabId,
    type: 'button',
    onClick,
    tabIndex,
    disabled
  };
};
export const getTabPanelProps = (activeTab, tabIndex) => {
  const tabPanelLabelId = generateId(activeTab, 'tabPanel');
  const tabPanelLabelledBy = generateId(activeTab, 'tab');
  return {
    role: 'tabpanel',
    tabIndex,
    'aria-labelledby': tabPanelLabelledBy,
    id: tabPanelLabelId,
    className: styles.tabPanel
  };
};
const generateId = (key, role) => `react-aria-${role}-${key}`;